import { EChartsOption } from 'echarts';
import { echarts } from '@/app/charts/echarts';
import {
  GaAccountMode,
  GaAccountModeKpiCard,
  GaAccountModeReport,
  GaAccountModeRow,
  GaPlatform,
  GaPlatformKpiAccent,
  GaPlatformKpiCard,
  GaPlatformReport,
  GaPlatformRow,
  GaLocationCountryCard,
  GaLocationCountryRow,
  GaLocationRow,
} from './account-mode.types';

export const GA_ACCOUNT_MODES: readonly GaAccountMode[] = ['creator', 'brand', 'agency'];

const PIE_GRADIENTS: Record<GaAccountMode, { offset: number; color: string }[]> = {
  creator: [
    { offset: 0, color: '#38bdf8' },
    { offset: 1, color: '#0369a1' },
  ],
  brand: [
    { offset: 0, color: '#fb7185' },
    { offset: 1, color: '#be123c' },
  ],
  agency: [
    { offset: 0, color: '#fbbf24' },
    { offset: 1, color: '#b45309' },
  ],
};

export function accountModeLabel(mode: string): string {
  if (mode === 'creator') {
    return 'Creator';
  }
  if (mode === 'brand') {
    return 'Brand';
  }
  if (mode === 'agency') {
    return 'Agency';
  }
  return mode;
}

export function rowForMode(rows: GaAccountModeRow[], mode: GaAccountMode): GaAccountModeRow {
  return (
    rows.find((row) => row.accountMode === mode) ?? {
      accountMode: mode,
      activeUsers: 0,
      eventCount: 0,
    }
  );
}

export function buildAccountModeKpiCards(
  current: GaAccountModeReport | null,
  previous: GaAccountModeReport | null,
): GaAccountModeKpiCard[] {
  const currentRows = current?.rows ?? [];
  const previousRows = previous?.rows ?? [];

  const modeCards: GaAccountModeKpiCard[] = GA_ACCOUNT_MODES.map((mode) => {
    const currentRow = rowForMode(currentRows, mode);
    const previousRow = rowForMode(previousRows, mode);
    return {
      key: mode,
      label: accountModeLabel(mode),
      current: currentRow.activeUsers,
      previous: previousRow.activeUsers,
      events: currentRow.eventCount,
      accent: mode === 'brand' ? 'pink' : 'violet',
    };
  });

  return [
    ...modeCards,
    {
      key: 'total',
      label: 'Total',
      current: current?.totals.activeUsers ?? 0,
      previous: previous?.totals.activeUsers ?? 0,
      events: current?.totals.eventCount ?? 0,
      accent: 'pink',
    },
  ];
}

export function pieChartTheme(isDarkMode: boolean): {
  borderColor: string;
  legendColor: string;
  emphasisColor: string;
} {
  if (isDarkMode) {
    return {
      borderColor: '#181823',
      legendColor: '#e4e4e7',
      emphasisColor: '#fafafa',
    };
  }

  return {
    borderColor: '#ffffff',
    legendColor: '#18181b',
    emphasisColor: '#09090b',
  };
}

export function buildAccountModePieOption(
  report: GaAccountModeReport | null,
  isDarkMode: boolean,
): { option: EChartsOption; hasData: boolean } {
  const rows = report?.rows ?? [];
  const data = GA_ACCOUNT_MODES.map((mode) => {
    const row = rowForMode(rows, mode);
    return {
      name: accountModeLabel(mode),
      value: row.activeUsers,
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, PIE_GRADIENTS[mode]),
      },
    };
  }).filter((slice) => slice.value > 0);

  const { borderColor, legendColor, emphasisColor } = pieChartTheme(isDarkMode);

  const option: EChartsOption = {
    tooltip: {
      trigger: 'item',
    },
    backgroundColor: 'transparent',
    legend: {
      top: '5%',
      left: 'center',
      textStyle: { color: legendColor, fontWeight: 600 },
    },
    series: [
      {
        name: 'Account mode',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor,
          borderWidth: 4,
        },
        label: {
          show: false,
          position: 'center',
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 28,
            fontWeight: 'bold',
            color: emphasisColor,
          },
        },
        labelLine: {
          show: false,
        },
        data,
      },
    ],
  };

  return { option, hasData: data.length > 0 };
}

const PLATFORM_GRADIENTS: Record<GaPlatform, { offset: number; color: string }[]> = {
  Android: [
    { offset: 0, color: '#4ade80' },
    { offset: 1, color: '#15803d' },
  ],
  iOS: [
    { offset: 0, color: '#e5e7eb' },
    { offset: 1, color: '#6b7280' },
  ],
};

export const GA_PLATFORMS: readonly GaPlatform[] = ['Android', 'iOS'];

export const PLATFORM_LEGEND_ICONS: Record<GaPlatform, string> = {
  Android: 'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/other/android-logo.png',
  iOS: 'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/other/apple-logo.png',
};

/** White Apple mark on a dark canvas; invert for light-mode legends. */
const APPLE_LOGO_LIGHT =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#18181b"><path d="M16.365 12.84c-.02-2.14 1.75-3.17 1.83-3.22-1-1.46-2.56-1.66-3.11-1.68-1.32-.13-2.58.78-3.25.78-.67 0-1.71-.76-2.81-.74-1.45.02-2.79.84-3.54 2.14-1.51 2.62-.39 6.5 1.08 8.63.72 1.04 1.58 2.2 2.71 2.16 1.09-.04 1.5-.7 2.81-.7 1.31 0 1.68.7 2.82.68 1.17-.02 1.91-1.06 2.62-2.1.83-1.21 1.17-2.38 1.19-2.44-.03-.01-2.27-.87-2.29-3.45zM14.7 6.43c.6-.73 1-1.74.89-2.75-.86.03-1.9.57-2.52 1.3-.55.64-1.04 1.67-.91 2.66.96.07 1.94-.49 2.54-1.21z"/></svg>',
  );

export function platformLegendIcon(platform: GaPlatform, isDarkMode: boolean): string {
  if (platform === 'iOS' && !isDarkMode) {
    return APPLE_LOGO_LIGHT;
  }
  return PLATFORM_LEGEND_ICONS[platform];
}

export function rowForPlatform(rows: GaPlatformRow[], platform: GaPlatform): GaPlatformRow {
  return (
    rows.find((row) => row.platform === platform) ?? {
      platform,
      activeUsers: 0,
      eventCount: 0,
    }
  );
}

export function platformKpiAccentDotClass(accent: GaPlatformKpiAccent): string {
  return accent === 'green' ? 'bg-green-400' : 'bg-zinc-300 dark:bg-zinc-400';
}

export function buildPlatformKpiCards(
  current: GaPlatformReport | null,
  previous: GaPlatformReport | null,
): GaPlatformKpiCard[] {
  const currentRows = current?.rows ?? [];
  const previousRows = previous?.rows ?? [];

  return GA_PLATFORMS.map((platform) => {
    const currentRow = rowForPlatform(currentRows, platform);
    const previousRow = rowForPlatform(previousRows, platform);
    return {
      key: platform,
      label: platform,
      current: currentRow.activeUsers,
      previous: previousRow.activeUsers,
      events: currentRow.eventCount,
      accent: platform === 'Android' ? 'green' : 'silver',
    };
  });
}

export function buildPlatformPieOption(
  report: GaPlatformReport | null,
  isDarkMode: boolean,
): { option: EChartsOption; hasData: boolean } {
  const rows = report?.rows ?? [];
  const data = GA_PLATFORMS.map((platform) => {
    const row = rowForPlatform(rows, platform);
    return {
      name: platform,
      value: row.activeUsers,
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, PLATFORM_GRADIENTS[platform]),
      },
    };
  }).filter((slice) => slice.value > 0);

  const { borderColor, legendColor, emphasisColor } = pieChartTheme(isDarkMode);

  const option: EChartsOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
    },
    legend: {
      top: '5%',
      left: 'center',
      itemWidth: 22,
      itemHeight: 22,
      itemGap: 28,
      data: data.map((slice) => ({
        name: slice.name,
        icon: `image://${platformLegendIcon(slice.name as GaPlatform, isDarkMode)}`,
      })),
      textStyle: { color: legendColor, fontWeight: 600, padding: [0, 0, 0, 4] },
    },
    series: [
      {
        name: 'Platform',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor,
          borderWidth: 4,
        },
        label: {
          show: false,
          position: 'center',
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 28,
            fontWeight: 'bold',
            color: emphasisColor,
          },
        },
        labelLine: {
          show: false,
        },
        data,
      },
    ],
  };

  return { option, hasData: data.length > 0 };
}

export function readGaErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') {
    return fallback;
  }

  const httpErr = err as {
    message?: string;
    error?: { message?: string | string[] };
    status?: number;
    statusText?: string;
  };

  const nested = httpErr.error?.message;
  if (Array.isArray(nested) && nested.length > 0) {
    return nested.join(', ');
  }
  if (typeof nested === 'string' && nested.trim()) {
    return nested;
  }
  if (typeof httpErr.message === 'string' && httpErr.message.trim() && !httpErr.message.startsWith('Http failure')) {
    return httpErr.message;
  }
  if (httpErr.status && httpErr.statusText) {
    return `${httpErr.status} ${httpErr.statusText}`;
  }
  return fallback;
}

export function buildLocationCountryCards(
  countries: GaLocationCountryRow[],
  limit = 8,
): GaLocationCountryCard[] {
  const top = countries.slice(0, limit);
  const max = top.reduce((highest, row) => Math.max(highest, row.activeUsers), 0);
  return top.map((row) => ({
    ...row,
    sharePercent: max > 0 ? Math.round((row.activeUsers / max) * 100) : 0,
  }));
}

export function topLocationCities(rows: GaLocationRow[], limit = 15): GaLocationRow[] {
  return [...rows]
    .sort((a, b) => b.activeUsers - a.activeUsers)
    .slice(0, limit);
}
